import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { NavBarMain } from "..";
import SettingsSection from './SettingsSection';
import SettingsForm from './SettingsForm';
import ProxiesTable from './ProxiesTable';
import { useConfiguration } from '../../hooks/useConfiguration.hook';

export const Settings = ({ history }) => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('places_bookies');
  const [showForm, setShowForm] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [placerBookies, setPlacerBookies] = useState([]);
  const [filteredBookies, setFilteredBookies] = useState([]);
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [proxiesLoading, setProxiesLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const configurationService = useConfiguration();

  // Fetch placer bookies data on component mount
  useEffect(() => {
    const fetchPlacerBookies = async () => {
      try {
        setLoading(true);
        const response = await configurationService.getPlacerBookies();
        setPlacerBookies(response || []);
        setFilteredBookies(response || []);
      } catch (error) {
        // Error handled - keep empty array as fallback
        setPlacerBookies([]);
        setFilteredBookies([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPlacerBookies();
  }, [configurationService]);

  // Fetch proxies data when proxies section is active
  useEffect(() => {
    const fetchProxies = async () => {
      try {
        setProxiesLoading(true);
        const response = await configurationService.getProxies();
        setProxies(response || []);
      } catch (error) {
        // Error handled - keep empty array as fallback
        setProxies([]);
      } finally {
        setProxiesLoading(false);
      }
    };

    if (activeSection === 'proxies') {
      fetchProxies();
    }
  }, [activeSection, configurationService]);

  // Filter bookies based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredBookies(placerBookies);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = placerBookies.filter(bookie => {
        return (
          bookie.bookie?.toLowerCase().includes(query) ||
          bookie.country?.toLowerCase().includes(query) ||
          bookie.ogBookie?.toLowerCase().includes(query) ||
          bookie.priority?.toString().includes(query)
        );
      });
      setFilteredBookies(filtered);
    }
  }, [searchQuery, placerBookies]);

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Settings sections - including both placer bookies and proxies
  const settingsSections = [
    {
      id: 'places_bookies',
      title: 'Placer Bookies',
      description: 'Configuration settings for bookie places and betting parameters',
      icon: '🏟️',
      type: 'bookies',
      bookies: filteredBookies,
      loading: loading,
      totalCount: placerBookies.length,
      filteredCount: filteredBookies.length
    },
    {
      id: 'proxies',
      title: 'Proxies',
      description: 'View and manage proxy configurations and status',
      icon: '🌐',
      type: 'proxies',
      proxies: proxies,
      loading: proxiesLoading
    }
  ];

  const handleEditSetting = (setting, sectionId) => {
    setSelectedSetting({ ...setting, sectionId });
    setShowForm(true);
  };

  const handleSaveSetting = async (settingData) => {
    try {
      setFormLoading(true);
      
      if (selectedSetting.sectionId === 'places_bookies') {
        // Handle placer bookie update
        const bookieData = {
          shouldRound: settingData.shouldRound === true ? true : undefined,
          needsProtection: settingData.needsProtection === true ? true : undefined,
          needsProtectionForSecondBet: settingData.needsProtectionForSecondBet === true ? true : undefined,
          needsExtendedBalanceCheck: settingData.needsExtendedBalanceCheck === true ? true : undefined,
          tracing: settingData.tracing === true ? true : undefined,
          debugLoggingEnabled: settingData.debugLoggingEnabled === true ? true : undefined,
          noBalanceRestriction: settingData.noBalanceRestriction === true ? true : undefined,
          solo: settingData.solo === true ? true : undefined,
          blacklisted: settingData.blacklisted === true ? true : undefined,
          maxLogins: settingData.maxLogins ? parseInt(settingData.maxLogins) : undefined,
          logInInterval: settingData.logInInterval ? parseInt(settingData.logInInterval) : undefined,
          maxDaysPerProxy: settingData.maxDaysPerProxy ? parseInt(settingData.maxDaysPerProxy) : undefined,
          tokenRefreshInterval: settingData.tokenRefreshInterval ? parseInt(settingData.tokenRefreshInterval) : undefined,
          minimumProfit: settingData.minimumProfit ? parseFloat(settingData.minimumProfit) : undefined,
          maximumProfit: settingData.maximumProfit ? parseFloat(settingData.maximumProfit) : undefined,
          minimumStake: settingData.minimumStake ? parseFloat(settingData.minimumStake) : undefined,
          hardLimit: settingData.hardLimit ? parseFloat(settingData.hardLimit) : undefined,
          probabilityThreshold: settingData.probabilityThreshold ? parseFloat(settingData.probabilityThreshold) : undefined,
          maxBetExceededThreshold: settingData.maxBetExceededThreshold ? parseFloat(settingData.maxBetExceededThreshold) : undefined,
          rebetLiquidityScoreThreshold: settingData.rebetLiquidityScoreThreshold ? parseFloat(settingData.rebetLiquidityScoreThreshold) : undefined,
          clones: settingData.clones && settingData.clones.length > 0 ? settingData.clones : undefined,
        };

        // Remove any undefined fields
        Object.keys(bookieData).forEach(key => {
          if (bookieData[key] === undefined) {
            delete bookieData[key];
          }
        });

        await configurationService.updatePlacerBookie(settingData.bookie, bookieData);
        
        // Refresh the placer bookies data
        const response = await configurationService.getPlacerBookies();
        setPlacerBookies(response || []);
        setFilteredBookies(response || []);
        
        // TODO: Show success toast/notification
      } else {
        // Handle regular setting update
        // TODO: Implement regular settings update if needed
      }
      
      setShowForm(false);
      setSelectedSetting(null);
    } catch (error) {
      // Handle error
      // TODO: Show error toast/notification - for now we just keep the form open
      // The error will be logged by the fetch utility
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedSetting(null);
  };

  const currentSection = settingsSections.find(section => section.id === activeSection);

  return (
    <div className="min-h-screen bg-[#111E12]">
      <NavBarMain history={history} />
      
      <div className="container mx-auto px-4 py-8 max-w-[1600px]">
        <div className="mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
            <p className="text-[#ABCDDF]">Configure your placer bookies and system settings</p>
          </div>

          {/* Tabs Navigation */}
          <div className="mb-6">
            <div className="flex space-x-1 bg-[#2A3C29] rounded-lg p-1">
              {settingsSections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                    activeSection === section.id
                      ? 'bg-[#E8FC8B] text-[#1B2D1A]'
                      : 'text-[#ABCDDF] hover:text-white hover:bg-[#3A4C39]'
                  }`}
                >
                  <span className="mr-2">{section.icon}</span>
                  {section.title}
                </button>
              ))}
            </div>
          </div>

          {/* Search Bar - Only show for bookies section */}
          {activeSection === 'places_bookies' && (
            <div className="mb-6">
              <div className="relative max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search bookies by name, country, or priority..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 bg-[#2A3C29] text-white placeholder-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E8FC8B] focus:border-transparent transition-all duration-200"
                />
                {searchQuery && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
              {searchQuery && (
                <div className="mt-2 text-sm text-gray-400">
                  Showing {filteredBookies.length} of {placerBookies.length} bookies
                </div>
              )}
            </div>
          )}

          {/* Settings Content */}
          {currentSection?.type === 'proxies' ? (
            <div className="space-y-6">
              {/* Section Header */}
              <div className="bg-[#2A3C29] p-6 rounded-lg">
                <div className="flex items-center space-x-3 mb-2">
                  <span className="text-2xl">{currentSection.icon}</span>
                  <h2 className="text-xl font-semibold text-[#E8FC8B]">{currentSection.title}</h2>
                </div>
                <p className="text-[#ABCDDF]">{currentSection.description}</p>
              </div>

              {/* Proxies Table */}
              <div className="bg-[#2A3C29] p-6 rounded-lg">
                <ProxiesTable proxies={currentSection.proxies} loading={currentSection.loading} />
              </div>
            </div>
          ) : (
            <SettingsSection 
              section={currentSection} 
              onEditSetting={handleEditSetting}
            />
          )}

          {/* Settings Form Modal */}
          <SettingsForm
            show={showForm}
            onHide={handleCloseForm}
            setting={selectedSetting}
            onSubmit={handleSaveSetting}
            loading={formLoading}
          />
        </div>
      </div>
    </div>
  );
}; 